import React from 'react';
import { ArrowLeft, Heart, Award, Users, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF9F7] pt-32 pb-16">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          className="mb-6 text-[#2C2C2C] hover:text-[#C4B5A0]"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-seasons font-light text-[#2C2C2C] mb-6 leading-tight">
              Our Story of
              <br />
              <span className="text-[#6B4E37]">Timeless Elegance</span>
            </h1>
            <p className="text-xl text-[#8B7D6B] font-montserrat max-w-3xl mx-auto leading-relaxed">
              Four decades of crafting dreams, one stitch at a time
            </p>
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Left Column - Story */}
            <div className="space-y-6">
              <Card className="bg-white border-none shadow-lg p-8">
                <h2 className="text-3xl font-seasons text-[#2C2C2C] mb-6">
                  A Legacy Since 1985
                </h2>
                <div className="space-y-4 text-[#2C2C2C] font-montserrat leading-relaxed">
                  <p>
                    In the heart of Delhi's historic Chandni Chowk, Sachdeva Sons was born in 1985 with a vision to bring the finest ethnic wear to women who appreciate the art of traditional Indian craftsmanship. What started as a humble shop has grown into a trusted name, synonymous with quality, elegance, and authenticity.
                  </p>
                  <p>
                    For over 40 years, we have been weaving dreams into reality, one garment at a time. Our journey has been guided by an unwavering commitment to preserving India's rich textile heritage while embracing contemporary design sensibilities.
                  </p>
                  <p>
                    Every piece in our collection tells a story – of skilled artisans who pour their heart into intricate embroidery, of master weavers who create magic with threads, and of a family dedicated to keeping traditional craftsmanship alive in the modern world.
                  </p>
                </div>
              </Card>
            </div>

            {/* Right Column - Values */}
            <div className="space-y-6">
              <Card className="bg-[#F5F3F0] border-none shadow-lg p-8">
                <h2 className="text-3xl font-seasons text-[#2C2C2C] mb-6">
                  Our Values
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-full h-fit">
                      <Heart className="h-6 w-6 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                        Passion for Excellence
                      </h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">
                        We believe that every woman deserves to feel beautiful and confident in what she wears. This drives us to curate only the finest pieces.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-full h-fit">
                      <Award className="h-6 w-6 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                        Authentic Craftsmanship
                      </h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">
                        We work directly with skilled artisans across India, ensuring that each garment meets our exacting standards of quality and authenticity.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-full h-fit">
                      <Users className="h-6 w-6 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                        Customer-Centric Approach
                      </h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">
                        Your satisfaction is our success. We build lasting relationships with our customers through personalized service and attention to detail.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-full h-fit">
                      <Sparkles className="h-6 w-6 text-[#C4B5A0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                        Timeless Elegance
                      </h3>
                      <p className="text-sm text-[#2C2C2C] font-montserrat">
                        We blend traditional aesthetics with contemporary styles, creating pieces that transcend trends and remain elegant for years to come.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* What We Offer Section */}
          <Card className="bg-white border-none shadow-lg p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-seasons text-[#2C2C2C] mb-8 text-center">
              What We Offer
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-[#F5F3F0] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-seasons text-[#C4B5A0]">12+</span>
                </div>
                <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                  Diverse Collections
                </h3>
                <p className="text-sm text-[#8B7D6B] font-montserrat">
                  From Chanderi and Silk to Georgette and Pakistani suits, we offer a wide range of premium ethnic wear
                </p>
              </div>

              <div className="text-center">
                <div className="bg-[#F5F3F0] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-seasons text-[#C4B5A0]">500+</span>
                </div>
                <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                  Unique Designs
                </h3>
                <p className="text-sm text-[#8B7D6B] font-montserrat">
                  Constantly refreshed collection featuring exclusive designs and patterns
                </p>
              </div>

              <div className="text-center">
                <div className="bg-[#F5F3F0] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-seasons text-[#C4B5A0]">25K+</span>
                </div>
                <h3 className="font-semibold font-montserrat text-[#6B4E37] mb-2">
                  Happy Customers
                </h3>
                <p className="text-sm text-[#8B7D6B] font-montserrat">
                  Trusted by thousands of women across India and worldwide
                </p>
              </div>
            </div>
          </Card>

          {/* Our Promise Section */}
          <Card className="bg-gradient-to-br from-[#F5F3F0] to-[#FAF9F7] border-none shadow-lg p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-seasons text-[#2C2C2C] mb-6 text-center">
              Our Promise to You
            </h2>
            <div className="max-w-3xl mx-auto space-y-4 text-[#2C2C2C] font-montserrat leading-relaxed text-center">
              <p>
                At Sachdeva Sons, we don't just sell ethnic wear – we help you celebrate life's precious moments in style. Whether it's a wedding, festival, or special celebration, we ensure that you look and feel your absolute best.
              </p>
              <p>
                Every garment we offer has been carefully selected and inspected to meet our high standards. We believe in transparency, authenticity, and building relationships that last beyond a single purchase.
              </p>
              <p className="text-lg font-semibold text-[#6B4E37] pt-4">
                When you choose Sachdeva Sons, you're not just buying a dress – you're becoming part of a legacy that celebrates the beauty of Indian craftsmanship.
              </p>
            </div>
          </Card>

          {/* Visit Us Section */}
          <Card className="bg-white border-none shadow-lg p-8 md:p-12 text-center">
            <h2 className="text-3xl font-seasons text-[#2C2C2C] mb-4">
              Visit Our Store
            </h2>
            <p className="text-[#8B7D6B] font-montserrat mb-6">
              Experience our collection in person at our flagship store in Delhi's historic Chandni Chowk
            </p>
            <div className="space-y-2 mb-8">
              <p className="text-[#2C2C2C] font-montserrat">
                <strong>Address:</strong> 52, Krishna Cloth Market, Chandni Chowk, Delhi - 110006
              </p>
              <p className="text-[#2C2C2C] font-montserrat">
                <strong>Hours:</strong> Monday - Saturday: 11:30 AM - 8:00 PM | Sunday: CLOSED
              </p>
              <p className="text-[#2C2C2C] font-montserrat">
                <strong>Phone:</strong> +91 9811193388
              </p>
            </div>
            <Button
              className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase px-8 py-6"
              onClick={() => navigate('/contact')}
            >
              Get in Touch
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};
